import React from 'react';
import { ArrowRight, Globe, Briefcase, Code, BarChart as ChartBar, Coins, Shield, Plane } from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import WalletMenu from '../components/WalletMenu';
import NavigationMenu from '../components/NavigationMenu';
import ThemeToggle from '../components/ThemeToggle';

export default function BehindTheScene() {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-8">
              <Logo />
              <NavigationMenu />
            </div>
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Behind the Scenes</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are dedicated to excellence, precision, and seamless experiences. Our team of charter specialists, logistics experts, and customer support professionals work tirelessly to ensure every journey is flawless, embodying the passion and commitment that propel PrivateCharterX forward.
            </p>
          </div>

          {/* Team Section - Redesigned */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Leadership Team</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-b from-gray-50 to-white p-8 rounded-2xl text-center shadow-lg border border-gray-100 transform transition-transform hover:scale-105">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-6">
                  <Briefcase className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">Lorenzo Vanza</h3>
                <p className="text-gray-600 font-medium mb-1">Founder & CEO</p>
                <p className="text-sm text-gray-500 mb-4">Switzerland</p>
                <p className="text-gray-600">
                  With over 12 years in business development, Lorenzo brings unparalleled expertise and vision to PrivateCharterX.
                </p>
              </div>
              
              <div className="bg-gradient-to-b from-gray-50 to-white p-8 rounded-2xl text-center shadow-lg border border-gray-100 transform transition-transform hover:scale-105">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-6">
                  <ChartBar className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">Andrin Schaufelberger</h3>
                <p className="text-gray-600 font-medium mb-1">Chief Financial Officer</p>
                <p className="text-sm text-gray-500 mb-4">Switzerland</p>
                <p className="text-gray-600">
                  With 26 years of CEO experience and extensive knowledge in financial strategy, Andrin leads our financial operations.
                </p>
              </div>

              <div className="bg-gradient-to-b from-gray-50 to-white p-8 rounded-2xl text-center shadow-lg border border-gray-100 transform transition-transform hover:scale-105">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-6">
                  <Globe className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">Claudio Steyskal</h3>
                <p className="text-gray-600 font-medium mb-1">Head of Branch, Switzerland</p>
                <p className="text-sm text-gray-500 mb-4">Switzerland</p>
                <p className="text-gray-600">
                  Leading our Swiss operations, Claudio brings strategic vision and operational excellence to our core market.
                </p>
              </div>

              {/* Marketing & Technology */}
              <div className="bg-gradient-to-b from-gray-50 to-white p-8 rounded-2xl text-center shadow-lg border border-gray-100 transform transition-transform hover:scale-105">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-6">
                  <ChartBar className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">Moreno Guerini</h3>
                <p className="text-gray-600 font-medium mb-1">Head of Marketing</p>
                <p className="text-sm text-gray-500 mb-4">Switzerland</p>
                <p className="text-gray-600">
                  A creative force in luxury marketing, Moreno crafts our brand strategy and digital presence.
                </p>
              </div>

              <div className="bg-gradient-to-b from-gray-50 to-white p-8 rounded-2xl text-center shadow-lg border border-gray-100 transform transition-transform hover:scale-105">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-6">
                  <Code className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">Simon Kulik</h3>
                <p className="text-gray-600 font-medium mb-1">IT Specialist & Crypto Expert</p>
                <p className="text-sm text-gray-500 mb-4">Germany</p>
                <p className="text-gray-600">
                  Leading our IT and marketing initiatives, Simon brings extensive knowledge in technology and digital strategy.
                </p>
              </div>

              <div className="bg-gradient-to-b from-gray-50 to-white p-8 rounded-2xl text-center shadow-lg border border-gray-100 transform transition-transform hover:scale-105">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-6">
                  <Shield className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">Marisa Clarks</h3>
                <p className="text-gray-600 font-medium mb-1">Head of Administration</p>
                <p className="text-sm text-gray-500 mb-4">United Kingdom</p>
                <p className="text-gray-600">
                  With meticulous attention to detail, Marisa oversees our administrative operations.
                </p>
              </div>
            </div>
          </div>

          {/* Web3 & Tokenization Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Web3 & Tokenization</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-2xl border border-blue-100">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <Coins size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">PVCX Token Ecosystem</h3>
                    <p className="text-gray-600">Revolutionizing loyalty in private aviation</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  Our PVCX token creates a seamless loyalty system where customers earn tokens for every kilometer flown. These tokens can be used for discounts, upgrades, and exclusive services within our ecosystem.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500 flex-shrink-0 mt-0.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span className="text-gray-600">Earn 1.5 PVCX tokens per kilometer flown</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500 flex-shrink-0 mt-0.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span className="text-gray-600">Redeem for flight discounts and premium services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500 flex-shrink-0 mt-0.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span className="text-gray-600">Transparent, blockchain-based loyalty program</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-teal-50 p-8 rounded-2xl border border-green-100">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <Plane size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">CO2 Certificates on Blockchain</h3>
                    <p className="text-gray-600">Transparent carbon offset tracking</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  We're committed to environmental responsibility. Our blockchain-based CO2 certificates allow customers to track and verify their carbon offsets with complete transparency.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500 flex-shrink-0 mt-0.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span className="text-gray-600">Automatic carbon footprint calculation for each flight</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500 flex-shrink-0 mt-0.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span className="text-gray-600">Verifiable carbon offset certificates as NFTs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500 flex-shrink-0 mt-0.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span className="text-gray-600">Support for verified carbon offset projects worldwide</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Flight NFTs Section */}
          <div className="mb-16">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-full md:w-1/2">
                <img 
                  src="https://images.unsplash.com/photo-1639762681057-408e52192e55?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2232&q=80" 
                  alt="Flight NFTs" 
                  className="rounded-2xl shadow-lg w-full h-[400px] object-cover"
                />
              </div>
              <div className="w-full md:w-1/2">
                <h2 className="text-3xl font-bold mb-4">Flight NFTs</h2>
                <p className="text-base text-gray-600 mb-6">
                  Every flight with PrivateCharterX is recorded as a unique NFT on the blockchain, creating a permanent, verifiable record of your journey. These NFTs serve as digital memorabilia and can be collected, displayed, or traded.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-2">Verifiable Flight Records</h3>
                    <p className="text-base text-gray-600">
                      Each NFT contains detailed flight information including route, aircraft, date, and carbon offset data, all cryptographically secured on the blockchain.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-2">Digital Collectibles</h3>
                    <p className="text-base text-gray-600">
                      Build your collection of flight experiences with beautifully designed NFTs that commemorate each journey.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-2">Exclusive Benefits</h3>
                    <p className="text-base text-gray-600">
                      Flight NFT holders gain access to special events, priority booking windows, and other exclusive perks within our ecosystem.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Miles & More Blockchain Section */}
          <div className="mb-16">
            <div className="flex flex-col md:flex-row-reverse items-center gap-8">
              <div className="w-full md:w-1/2">
                <img 
                  src="https://images.unsplash.com/photo-1551721434-8b94ddff0e6d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80" 
                  alt="Blockchain Miles" 
                  className="rounded-2xl shadow-lg w-full h-[400px] object-cover"
                />
              </div>
              <div className="w-full md:w-1/2">
                <h2 className="text-3xl font-bold mb-4">Blockchain Miles & More</h2>
                <p className="text-base text-gray-600 mb-6">
                  Our revolutionary blockchain-based loyalty program transforms how frequent flyers earn and redeem miles. Unlike traditional programs, our system offers complete transparency, instant rewards, and flexible redemption options.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-2">Instant Rewards</h3>
                    <p className="text-base text-gray-600">
                      Miles are credited to your wallet immediately after your flight, with no waiting periods or processing delays.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-2">Transferable Assets</h3>
                    <p className="text-base text-gray-600">
                      Your miles are truly yours - transfer them to friends or family, or convert them to other cryptocurrencies.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-2">Smart Redemption</h3>
                    <p className="text-base text-gray-600">
                      Smart contracts ensure fair and consistent redemption values, with no blackout dates or hidden restrictions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Join Us CTA */}
          <div className="bg-gradient-to-br from-black to-gray-800 text-white p-12 rounded-2xl text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-black opacity-10"></div>
            <div className="relative z-10">
              <Globe size={48} className="mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4">Stay Connected</h3>
              <p className="text-base mb-8 max-w-2xl mx-auto">
                Follow our journey and stay updated with the latest news, insights, and exclusive offers in private aviation. Visit our blog for expert perspectives and industry updates.
              </p>
              <a 
                href="https://privatecharterx.blog"
                target="_blank"
                rel="noopener noreferrer" 
                className="inline-block bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Visit Our Blog
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}